import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Users, Stethoscope, Loader2, Sparkles, UploadCloud, BookOpen, TrendingUp,
  AlertTriangle, MessageSquareQuote, ExternalLink,
} from "lucide-react";

const pct = (v) => `${Number(v || 0).toFixed(1)} %`;
const eur = (v) => `${Number(v || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`;

function Bar({ value, best }) {
  const w = best ? Math.min(100, (value / best) * 100) : 0;
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full bg-slate-900" style={{ width: `${w}%` }} />
    </div>
  );
}

function Nomenclator({ data, onUploaded, canUpload, nomenclatorLoaded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const ref = useRef(null);
  const atcRef = useRef(null);
  const factRef = useRef(null);

  const upload = async (file, input = null) => {
    if (!file) return;
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/consejo/indications/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo leer el Excel");
    } finally {
      setBusy(false);
      if (input?.current) input.current.value = "";
    }
  };

  const uploadAtc = async (file, endpoint = "/nomenclator/upload", input = null) => {
    if (!file) return;
    setBusy(true); setErr(""); setOk("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setOk(data.productos
        ? `${data.productos.toLocaleString("es-ES")} productos importados (${(data.con_familia || 0).toLocaleString("es-ES")} con patología identificada).`
        : "Nomenclátor importado.");
      onUploaded();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo importar el nomenclátor");
    } finally {
      setBusy(false);
      if (input?.current) input.current.value = "";
    }
  };

  return (
    <div className="card p-5 mb-6" data-testid="nomenclator-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><BookOpen size={18} /></div>
          <div>
            <h2 className="font-display text-lg font-bold">Nomenclátor de indicaciones terapéuticas</h2>
            <p className="text-sm text-slate-600">
              {data ? `${data.meta?.total || data.rows.length} indicaciones · ${data.families.length} familias · ${Object.keys(data.advice || {}).length} con consejo de referencia` : "Cargando…"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={() => setOpen((v) => !v)} data-testid="toggle-nomenclator">{open ? "Ocultar" : "Ver tabla"}</button>
          {canUpload && (
            <>
              <input ref={factRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={(e) => uploadAtc(e.target.files?.[0], "/consejo/nomenclator-facturacion", factRef)} data-testid="facturacion-file-input" />
              <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => factRef.current?.click()} data-testid="upload-facturacion-button">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Nomenclátor de Facturación
              </button>
            </>
          )}
          <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files?.[0], ref)} data-testid="indications-file-input" />
          {canUpload && (
            <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => ref.current?.click()} data-testid="upload-indications-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Subir Excel de recomendaciones
            </button>
          )}
        </div>
      </div>
      {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
      {ok && <div className="mt-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="nomenclator-ok">{ok}</div>}
      {nomenclatorLoaded === 0 && (
        <div className="mt-3 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-3 flex items-start gap-3 flex-wrap" data-testid="nomenclator-missing-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="font-semibold">El nomenclátor de medicamentos no está cargado en este entorno</div>
            <div className="text-xs mt-0.5">Sin él, las recetas se clasifican solo por el nombre del medicamento y algunas salen como «Sin clasificar». Importa el <strong>Nomenclátor de Facturación</strong> (.xls del SNS) o tu Excel con códigos ATC para clasificarlas todas.</div>
          </div>
          {canUpload ? (
            <div className="flex flex-col items-end gap-1">
              <input ref={atcRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadAtc(e.target.files?.[0], "/nomenclator/upload", atcRef)} data-testid="nomenclator-file-input" />
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => factRef.current?.click()} data-testid="upload-nomenclator-button">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Importar Nomenclátor de Facturación
              </button>
              <button className="text-[11px] underline text-amber-900/70 hover:text-amber-900" disabled={busy} onClick={() => atcRef.current?.click()} data-testid="upload-atc-link">
                o subir el Excel con códigos ATC
              </button>
            </div>
          ) : (
            <span className="text-xs font-semibold">Contacta con el administrador para importarlo.</span>
          )}
        </div>
      )}
      {open && data && (
        <div className="mt-4 max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
          <table className="dense" data-testid="indications-table">
            <thead><tr><th>Indicación</th><th>Familia</th><th>Consejo de referencia</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td className="font-semibold">{r.indicacion}</td>
                  <td>{r.familia}</td>
                  <td className="text-xs text-slate-600">{r.consejo || data.advice?.[r.familia]?.consejo || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ConsejoEmpleados({ embedded = false }) {
  const { user } = useAuth();
  const [imports, setImports] = useState([]);
  const [importId, setImportId] = useState("");
  const [data, setData] = useState(null);
  const [nom, setNom] = useState(null);
  const [employee, setEmployee] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [planes, setPlanes] = useState([]);
  const [aiState, setAiState] = useState("idle");
  const runRef = useRef(0);

  useEffect(() => {
    runRef.current += 1;
    setPlanes([]); setAiState("idle"); setError("");
  }, [employee]);

  const loadNom = () => api.get("/consejo/indications").then((r) => setNom(r.data)).catch(() => {});

  useEffect(() => {
    loadNom();
    api.get("/sales/imports").then((r) => {
      setImports(r.data);
      if (r.data.length) setImportId(r.data[0].id);
    }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const reloadAnalysis = () => {
    if (!importId) return;
    runRef.current += 1;
    setLoading(true); setError(""); setPlanes([]); setAiState("idle");
    api.get("/consejo/employee-analysis", { params: { import_id: importId } })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadAnalysis();
    /* eslint-disable-next-line */
  }, [importId]);

  const generar = async () => {
    setAiState("running"); setError(""); setPlanes([]);
    const runId = ++runRef.current;
    try {
      await api.post("/consejo/coaching", { import_id: importId, employee: employee || null });
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        if (runRef.current !== runId) return;
        const { data: d } = await api.get(`/consejo/coaching/${importId}`, { params: employee ? { employee } : {} });
        if (runRef.current !== runId) return;
        if (d.status === "done") {
          setPlanes(d.planes || []);
          setAiState("done");
          if (!(d.planes || []).length) setError("La IA no ha encontrado propuestas para este filtro");
          return;
        }
        if (d.status === "error") { setError(d.error || "No se pudieron generar los planes"); setAiState("idle"); return; }
      }
      setAiState("idle"); setError("La IA ha tardado demasiado, vuelve a intentarlo");
    } catch (e) {
      setAiState("idle");
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const detalle = useMemo(() => {
    if (!data) return [];
    return employee ? data.detalle.filter((d) => d.empleado === employee) : data.detalle;
  }, [data, employee]);

  const oportunidades = useMemo(() => {
    if (!data) return [];
    if (!employee) return data.oportunidades;
    const top = data.oportunidades.filter((o) => o.empleado === employee);
    if (top.length) return top;
    // Fuera del top-25: las calculamos igual que el backend, desde el detalle
    const famRatio = Object.fromEntries(data.familias.map((f) => [f.familia, f.ratio_consejo]));
    return data.detalle
      .filter((d) => d.empleado === employee && d.operaciones >= 3 && d.familia !== "Sin clasificar")
      .map((d) => {
        const objetivo = famRatio[d.familia] ?? data.media_consejo;
        return { ...d, ratio_familia: objetivo, consejos_potenciales: Math.round((objetivo - d.ratio_consejo) / 100 * d.operaciones * 10) / 10 };
      })
      .filter((d) => d.consejos_potenciales > 0)
      .sort((a, b) => b.consejos_potenciales - a.consejos_potenciales);
  }, [data, employee]);

  const kpis = useMemo(() => {
    if (!data) return null;
    if (!employee) {
      return {
        ratio: data.media_consejo,
        ops: data.operaciones_receta,
        fams: data.patologias_detectadas ?? data.familias.length,
        oport: Math.round(data.oportunidades.reduce((a, o) => a + o.consejos_potenciales, 0)),
      };
    }
    const e = data.empleados.find((x) => x.empleado === employee);
    return {
      ratio: e?.ratio_consejo || 0,
      ops: e?.operaciones || 0,
      fams: detalle.filter((d) => d.familia !== "Sin clasificar").length,
      oport: Math.round(oportunidades.reduce((a, o) => a + o.consejos_potenciales, 0)),
    };
  }, [data, employee, detalle, oportunidades]);

  const bestFam = data?.familias?.[0]?.operaciones || 0;

  return (
    <div className={embedded ? "fade-in" : "p-8 fade-in"} data-testid="consejo-empleados-page">
      {!embedded && <>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Consejo</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Consejo por empleado y patología</h1>
      </>}
      <p className="text-slate-600 mb-6 max-w-3xl">
        Cruza cada receta dispensada con su familia terapéutica y mide quién acompaña la receta con consejo.
        Las propuestas se generan con IA sobre tu stock y con fuentes contrastadas; requieren revisión farmacéutica.
      </p>

      <Nomenclator data={nom} onUploaded={() => { loadNom(); if (importId) reloadAnalysis(); }}
                   canUpload={user?.role === "admin"} nomenclatorLoaded={data?.nomenclator_cargado} />

      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Mes</span>
          <select className="input" style={{ width: 280 }} value={importId} onChange={(e) => setImportId(e.target.value)} data-testid="import-select">
            {imports.map((i) => <option key={i.id} value={i.id}>{i.label || i.file_name}</option>)}
          </select>
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Empleado</span>
          <select className="input" style={{ width: 200 }} value={employee} onChange={(e) => setEmployee(e.target.value)} data-testid="employee-select">
            <option value="">Todo el equipo</option>
            {(data?.empleados || []).map((e) => <option key={e.empleado} value={e.empleado}>{e.empleado}</option>)}
          </select>
          <button className="btn btn-primary" disabled={!data || aiState === "running"} onClick={generar} data-testid="generate-coaching-button">
            {aiState === "running" ? <><Loader2 size={15} className="animate-spin" /> Generando…</> : <><Sparkles size={15} /> Plan de consejo con IA</>}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="consejo-emp-error">{error}</div>}
      {loading && <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Analizando el mes…</div>}
      {!loading && imports.length === 0 && (
        <div className="card p-8 text-center" data-testid="consejo-emp-empty">
          <Stethoscope size={28} className="mx-auto text-slate-300 mb-3" />
          <h2 className="font-display text-xl font-bold mb-1">Aún no hay ventas que analizar</h2>
          <p className="text-slate-600 text-sm">Sube un mes de ventas en «Subidas → Ventas mes a mes» y aquí verás el consejo por empleado y patología.</p>
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="consejo-kpis">
            {[
              { k: "media", label: "Ratio de consejo medio", v: pct(kpis.ratio) },
              { k: "ops", label: "Operaciones con receta", v: kpis.ops.toLocaleString("es-ES") },
              { k: "fams", label: "Patologías detectadas", v: kpis.fams },
              { k: "oport", label: "Consejos potenciales", v: kpis.oport },
            ].map((c) => (
              <div key={c.k} className="card p-4" data-testid={`kpi-${c.k}`}>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{c.label}</div>
                <div className="font-display text-2xl font-bold mt-1">{c.v}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div className="card p-5">
              <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Users size={17} /> Equipo</h2>
              <table className="dense" data-testid="employees-table">
                <thead><tr><th>Empleado</th><th className="text-right">Recetas</th><th className="text-right">Con consejo</th><th className="text-right">Ratio</th><th className="text-right">Venta libre</th></tr></thead>
                <tbody>
                  {data.empleados.map((e) => (
                    <tr key={e.empleado} className={employee === e.empleado ? "bg-slate-50" : ""} data-testid={`employee-row-${e.empleado}`}>
                      <td className="font-semibold">{e.empleado}</td>
                      <td className="text-right font-mono text-xs">{e.operaciones}</td>
                      <td className="text-right font-mono text-xs">{e.con_consejo}</td>
                      <td className={`text-right font-mono text-xs font-bold ${e.ratio_consejo >= data.media_consejo ? "text-emerald-700" : "text-amber-600"}`}>{pct(e.ratio_consejo)}</td>
                      <td className="text-right font-mono text-xs">{eur(e.importe_libre)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card p-5">
              <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Stethoscope size={17} /> Patologías más dispensadas</h2>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="families-list">
                {data.familias.slice(0, 16).map((f) => (
                  <div key={f.familia} data-testid={`family-${f.familia}`}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="font-semibold">{f.familia}</span>
                      <span className="font-mono text-xs text-slate-500">{f.operaciones} · consejo {pct(f.ratio_consejo)}</span>
                    </div>
                    <Bar value={f.operaciones} best={bestFam} />
                    {f.indicaciones?.length > 0 && <div className="text-[11px] text-slate-400 mt-0.5">{f.indicaciones.join(" · ")}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card p-5 mb-6">
            <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2"><TrendingUp size={17} /> Oportunidades por empleado y patología</h2>
            <p className="text-sm text-slate-600 mb-3">Dónde cada persona está por debajo del ratio de consejo de la farmacia en esa familia.</p>
            <table className="dense" data-testid="opportunities-table">
              <thead><tr><th>Empleado</th><th>Patología / familia</th><th className="text-right">Recetas</th><th className="text-right">Su ratio</th><th className="text-right">Ratio farmacia</th><th className="text-right">Consejos que faltan</th></tr></thead>
              <tbody>
                {oportunidades.map((o, i) => (
                  <tr key={i} data-testid={`opportunity-${o.empleado}-${o.familia}`}>
                    <td className="font-semibold">{o.empleado}</td>
                    <td>{o.familia}</td>
                    <td className="text-right font-mono text-xs">{o.operaciones}</td>
                    <td className="text-right font-mono text-xs text-amber-600 font-bold">{pct(o.ratio_consejo)}</td>
                    <td className="text-right font-mono text-xs">{pct(o.ratio_familia)}</td>
                    <td className="text-right font-mono text-xs font-bold">{o.consejos_potenciales}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card p-5 mb-6" data-testid="detail-card">
            <h2 className="font-display text-lg font-bold mb-1">Detalle por empleado y patología</h2>
            <p className="text-sm text-slate-600 mb-3">
              {employee ? `Todas las familias dispensadas por ${employee}.` : "Todas las combinaciones del mes, de mayor a menor volumen."}
              {data.lineas_receta ? ` ${data.lineas_sin_familia} de ${data.lineas_receta} líneas sin identificar en el nomenclátor (${(100 * (1 - data.lineas_sin_familia / data.lineas_receta)).toFixed(1)} % clasificadas).` : ""}
            </p>
            <div className="max-h-96 overflow-y-auto">
              <table className="dense" data-testid="detail-table">
                <thead><tr><th>Empleado</th><th>Patología / familia</th><th className="text-right">Recetas</th><th className="text-right">Con consejo</th><th className="text-right">Ratio</th><th className="text-right">Venta libre</th></tr></thead>
                <tbody>
                  {detalle.slice(0, 120).map((d, i) => (
                    <tr key={i} data-testid={`detail-${d.empleado}-${d.familia}`}>
                      <td className="font-semibold">{d.empleado}</td>
                      <td>{d.familia}</td>
                      <td className="text-right font-mono text-xs">{d.operaciones}</td>
                      <td className="text-right font-mono text-xs">{d.con_consejo}</td>
                      <td className="text-right font-mono text-xs">{pct(d.ratio_consejo)}</td>
                      <td className="text-right font-mono text-xs">{eur(d.importe_libre)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {planes.length > 0 && (
            <div className="space-y-4 mb-6" data-testid="coaching-plans">
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><Sparkles size={18} /> Plan de consejo del equipo</h2>
              {planes.map((p, i) => (
                <div key={i} className="card p-5" data-testid={`plan-${p.empleado}-${p.familia}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <h3 className="font-display text-lg font-bold">{p.empleado} · {p.familia}</h3>
                  </div>
                  {p.objetivo && <p className="text-sm text-slate-600 mt-1">{p.objetivo}</p>}
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    {(p.propuestas || []).map((pr, j) => (
                      <div key={j} className="border border-slate-200 rounded-lg p-3">
                        <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                          {pr.producto}
                          {pr.cn ? <span className="text-xs text-slate-400 font-mono">{pr.cn}</span> : null}
                          {pr.en_stock
                            ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">en stock {pr.stock}</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">consejo sin producto</span>}
                          {pr.stock_parado ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900">stock parado</span> : null}
                        </div>
                        {pr.motivo && <p className="text-xs text-slate-600 mt-1">{pr.motivo}</p>}
                        {pr.pregunta && <p className="text-xs mt-2"><span className="font-semibold">Pregunta: </span>{pr.pregunta}</p>}
                        {pr.frase_mostrador && (
                          <p className="text-sm mt-2 bg-slate-50 border-l-2 border-slate-900 pl-2 py-1 italic flex gap-2">
                            <MessageSquareQuote size={14} className="shrink-0 mt-0.5 text-slate-400" />{pr.frase_mostrador}
                          </p>
                        )}
                        {pr.alarma && (
                          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 flex gap-1">
                            <AlertTriangle size={12} className="shrink-0 mt-0.5" />{pr.alarma}
                          </p>
                        )}
                        {pr.fuente && <div className="text-[11px] text-slate-400 mt-2">{pr.fuente}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-500">Propuestas generadas con IA sobre tu stock y fuentes públicas (AEMPS/CIMA, SEFAC, portales autonómicos). Requieren revisión farmacéutica antes de usarse en el mostrador.</p>
            </div>
          )}

          {nom?.advice && (
            <div className="card p-5" data-testid="family-advice">
              <h2 className="font-display text-lg font-bold mb-3">Consejo de referencia por familia</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(nom.advice).map(([fam, a]) => (
                  <div key={fam} className="border border-slate-200 rounded-lg p-3">
                    <div className="font-semibold text-sm">{fam}</div>
                    <p className="text-xs text-slate-600 mt-1">{a.consejo}</p>
                    <p className="text-xs mt-2"><span className="font-semibold">Pregunta: </span>{a.pregunta}</p>
                    <p className="text-xs text-amber-800 mt-1">{a.alarma}</p>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mt-2">
                      {a.fuente} <ExternalLink size={10} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
